import { useEffect, useMemo, useRef, useState } from "react";
import { createSite, deleteSite, getCatalogItems, getServiceCatalog, getSites, updateSite } from "../api";
import ManagementFilterHeader, { formatManagementSearchInput, matchesManagementSearch, ManagementListLoading, makeManagementFilterOptions, matchesManagementFilter, sortManagementRows } from "../components/management/ManagementFilterHeader";
import useIncrementalRows from "../hooks/useIncrementalRows";
import ImportExportOverlay from "../components/management/ImportExportOverlay";
import VestaSelect from "../components/common/VestaSelect";
import { OverlayFieldError, OverlayFieldLabel, overlayFieldClass } from "../components/common/OverlayValidation";
import { findMunicipality, normalizePostalCodeValue, postalCodeCatalogOptions } from "../utils/customerCatalogs";
import { getUserPageLevel, readStoredUser, UNAUTHORIZED_LABEL } from "../utils/permissions";
import "../assets/css/windows.css";
import "../assets/css/overlay.css";
import "../assets/css/bookings.css";
import "../assets/css/management.css";
import "../assets/css/sites.css";

const EMPTY_DEFAULTS = {
  customer: {
    invoiceCustomerType: { enabled: true, value: "Privato" },
    paymentMethod: { enabled: false, value: "" },
    contactType: { enabled: true, required: false, value: "Cellulare" },
    countryPrefixId: { enabled: false, required: false, value: "" },
    documentType: { enabled: true, required: false, value: "Carta identità" },
    preferredLanguage: { enabled: false, required: false, value: "" },
    preferredRoomTypeId: { enabled: false, required: false, value: "" },
    preferredFloor: { enabled: false, required: false, value: "" },
    preferredBedTypeId: { enabled: false, required: false, value: "" },
    arrivalPreference: { enabled: false, required: false, value: "" },
    invoiceSameAsCustomer: { enabled: false, value: true },
  },
  booking: {
    paymentMethod: { enabled: false, value: "" },
    status: { enabled: true, value: "CONFIRMED" },
    stayDays: { enabled: true, value: "3" },
    fiscalDocument: { enabled: true, value: "INVOICE" },
    reversalDocument: { enabled: true, value: "CREDIT_NOTE" },
    autoBalanceFastCheckout: { enabled: true, value: true },
    defaultVatRate: { enabled: true, value: "22" },
    defaultServiceIds: [],
  },
};

const EMPTY_SITE = {
  description: "",
  siteNature: "",
  address: "",
  streetNumber: "",
  cap: "",
  city: "",
  province: "",
  phone: "",
  email: "",
  active: true,
  defaults: EMPTY_DEFAULTS,
};

const BOOKING_STATUS_OPTIONS = [
  { value: "PENDING", label: "In attesa" },
  { value: "CONFIRMED", label: "Confermata" },
  { value: "CHECKED_IN", label: "In house" },
  { value: "CHECKED_OUT", label: "Conclusa" },
  { value: "CANCELLED", label: "Cancellata" },
];

const SITE_NATURE_OPTIONS = [
  { value: "LEGAL", label: "Sede Legale" },
  { value: "ADMINISTRATIVE", label: "Sede Amministrativa" },
  { value: "OPERATIVE", label: "Sede Operativa" },
];

const FISCAL_DOCUMENT_OPTIONS = [
  { value: "INVOICE", label: "Fattura" },
  { value: "RECEIPT", label: "Ricevuta" },
];

const REVERSAL_DOCUMENT_OPTIONS = [
  { value: "CREDIT_NOTE", label: "Nota di Credito" },
  { value: "RECEIPT_REVERSAL", label: "Storno Ricevuta" },
];

function parseSiteNature(value) {
  const token = String(value || "").trim().toLocaleLowerCase("it");
  if (["legal", "sede legale"].includes(token)) return "LEGAL";
  if (["administrative", "amministrativa", "sede amministrativa"].includes(token)) return "ADMINISTRATIVE";
  if (["operative", "operativa", "sede operativa"].includes(token)) return "OPERATIVE";
  return String(value || "").trim().toUpperCase();
}

function siteNatureLabel(value) {
  return SITE_NATURE_OPTIONS.find((option) => option.value === String(value || "").toUpperCase())?.label || "-";
}

const EMPTY_FILTERS = {
  description: [],
  siteNature: [],
  city: [],
  province: [],
  phone: [],
  email: [],
};

const SITE_IMPORT_EXPORT_FIELDS = [
  { key: "description", label: "Descrizione", required: true },
  { key: "siteNature", label: "Natura Sede", required: true, aliases: ["Natura"], parse: parseSiteNature, exportValue: (row) => siteNatureLabel(row.siteNature) },
  { key: "address", label: "Indirizzo" },
  { key: "streetNumber", label: "Civico" },
  { key: "cap", label: "CAP" },
  { key: "city", label: "Città", aliases: ["Citta"] },
  { key: "province", label: "Provincia" },
  { key: "phone", label: "Telefono" },
  { key: "email", label: "Mail", aliases: ["Email"] },
  { key: "active", label: "Attiva", type: "boolean", aliases: ["Sede attiva", "Stato"] },
];

function cloneDefaults(defaults = EMPTY_DEFAULTS) {
  return {
    customer: {
      invoiceCustomerType: { ...EMPTY_DEFAULTS.customer.invoiceCustomerType, ...(defaults.customer?.invoiceCustomerType || defaults.customer?.customerType || {}) },
      paymentMethod: { ...EMPTY_DEFAULTS.customer.paymentMethod, ...(defaults.customer?.paymentMethod || {}) },
      contactType: { ...EMPTY_DEFAULTS.customer.contactType, ...(defaults.customer?.contactType || {}) },
      countryPrefixId: { ...EMPTY_DEFAULTS.customer.countryPrefixId, ...(defaults.customer?.countryPrefixId || {}) },
      documentType: { ...EMPTY_DEFAULTS.customer.documentType, ...(defaults.customer?.documentType || {}) },
      preferredLanguage: { ...EMPTY_DEFAULTS.customer.preferredLanguage, ...(defaults.customer?.preferredLanguage || {}) },
      preferredRoomTypeId: { ...EMPTY_DEFAULTS.customer.preferredRoomTypeId, ...(defaults.customer?.preferredRoomTypeId || {}) },
      preferredFloor: { ...EMPTY_DEFAULTS.customer.preferredFloor, ...(defaults.customer?.preferredFloor || {}) },
      preferredBedTypeId: { ...EMPTY_DEFAULTS.customer.preferredBedTypeId, ...(defaults.customer?.preferredBedTypeId || {}) },
      arrivalPreference: { ...EMPTY_DEFAULTS.customer.arrivalPreference, ...(defaults.customer?.arrivalPreference || {}) },
      invoiceSameAsCustomer: { ...EMPTY_DEFAULTS.customer.invoiceSameAsCustomer, ...(defaults.customer?.invoiceSameAsCustomer || {}) },
    },
    booking: {
      paymentMethod: { ...EMPTY_DEFAULTS.booking.paymentMethod, ...(defaults.booking?.paymentMethod || {}) },
      status: { ...EMPTY_DEFAULTS.booking.status, ...(defaults.booking?.status || {}) },
      stayDays: { ...EMPTY_DEFAULTS.booking.stayDays, ...(defaults.booking?.stayDays || {}) },
      fiscalDocument: { ...EMPTY_DEFAULTS.booking.fiscalDocument, ...(defaults.booking?.fiscalDocument || {}) },
      reversalDocument: { ...EMPTY_DEFAULTS.booking.reversalDocument, ...(defaults.booking?.reversalDocument || {}) },
      autoBalanceFastCheckout: { ...EMPTY_DEFAULTS.booking.autoBalanceFastCheckout, ...(defaults.booking?.autoBalanceFastCheckout || {}) },
      defaultVatRate: { ...EMPTY_DEFAULTS.booking.defaultVatRate, ...(defaults.booking?.defaultVatRate || {}) },
      defaultServiceIds: [...new Set((Array.isArray(defaults.booking?.defaultServiceIds) ? defaults.booking.defaultServiceIds : []).map((value) => String(value || "")).filter(Boolean))],
    },
  };
}

function normalizeSiteDraft(site = {}) {
  return { ...EMPTY_SITE, ...site, defaults: cloneDefaults(site.defaults) };
}

function displayValue(value) {
  const text = String(value || "").trim();
  return text || "-";
}

function catalogOptions(items = [], currentValue = "") {
  const current = String(currentValue || "").trim();
  const options = (items || [])
    .filter((item) => item.active !== false)
    .sort((a, b) => Number(a.order || 0) - Number(b.order || 0) || String(a.description || "").localeCompare(String(b.description || ""), "it"))
    .map((item) => ({ value: item.description, label: item.description }));
  if (current && !options.some((option) => String(option.value) === current)) {
    options.push({ value: current, label: current });
  }
  return options;
}


function catalogIdOptions(items = [], currentValue = "") {
  const current = String(currentValue || "").trim();
  const options = (items || [])
    .filter((item) => item.active !== false)
    .sort((a, b) => Number(a.order || 0) - Number(b.order || 0) || String(a.description || "").localeCompare(String(b.description || ""), "it"))
    .map((item) => ({ value: String(item._id), label: item.description }));
  if (current && !options.some((option) => option.value === current)) options.push({ value: current, label: current });
  return options;
}

function countryPrefixOptions(items = [], currentValue = "") {
  const current = String(currentValue || "").trim();
  const options = (items || [])
    .filter((item) => item.active !== false && String(item.prefix || "").trim())
    .sort((a, b) => String(a.description || "").localeCompare(String(b.description || ""), "it"))
    .map((item) => ({ value: String(item._id), label: `${item.description} (${String(item.prefix).startsWith("+") ? item.prefix : `+${item.prefix}`})` }));
  if (current && !options.some((option) => option.value === current)) options.push({ value: current, label: current });
  return options;
}

function selectOverlayFieldValue(event) {
  const target = event.target;
  if (!target || typeof target.select !== "function") return;
  if (target.tagName !== "INPUT" && target.tagName !== "TEXTAREA") return;
  const type = String(target.type || "").toLowerCase();
  if (["checkbox", "radio", "button", "submit", "date", "month"].includes(type)) return;
  requestAnimationFrame(() => target.select());
}

function OptionsMenu({ open, menuRef, disabled, confirming, canEdit, canDelete, blockedClass, onBlocked, onToggle, onNew, onReset, onImport, onExport, onStartConfirm, onCancelConfirm, onConfirm }) {
  return (
    <div className="management_options_menu" ref={menuRef}>
      <button type="button" className="management_options_button" onClick={onToggle} aria-label="Opzioni sedi" aria-expanded={open}>
        <span></span><span></span><span></span>
      </button>
      {open && (
        <div className="management_options_dropdown">
          <button type="button" className={`management_options_item${blockedClass(canEdit)}`} data-disabled-tooltip={UNAUTHORIZED_LABEL} onClick={canEdit ? onNew : onBlocked}>Nuova Sede</button>
          <button type="button" className="management_options_item" onClick={onReset}>Azzera ricerca e filtri</button>
          <button type="button" className={`management_options_item${blockedClass(canEdit)}`} data-disabled-tooltip={UNAUTHORIZED_LABEL} onClick={canEdit ? onImport : onBlocked}>Importa Valori</button>
          <button type="button" className="management_options_item" onClick={onExport}>Esporta Valori</button>
          {confirming ? (
            <div className="management_options_confirm_row">
              <button type="button" className="management_options_item is_neutral" onClick={onCancelConfirm}>Annulla</button>
              <button type="button" className={`management_options_item is_danger${blockedClass(canDelete)}`} data-disabled-tooltip={UNAUTHORIZED_LABEL} onClick={canDelete ? onConfirm : onBlocked}>Conferma</button>
            </div>
          ) : (
            <button type="button" className={`management_options_item is_danger${blockedClass(canDelete)}`} data-disabled-tooltip={UNAUTHORIZED_LABEL} disabled={disabled} onClick={canDelete ? onStartConfirm : onBlocked}>Eliminazione Multipla</button>
          )}
        </div>
      )}
    </div>
  );
}

function DefaultRow({ label, help, field, onChange, type = "text", options = [], allowRequired = false, min = 1, max = undefined, step = undefined }) {
  const normalizedField = { ...field, enabled: true };
  return (
    <div className="site_default_row">
      <div className="site_default_copy">
        <strong>{label}</strong>
        <span>{help}</span>
      </div>
      <div className="site_default_row_controls">
        <div className="site_default_value_box">
          {type === "checkbox" ? (
            <VestaSelect value={normalizedField.value === false ? "NO" : "SI"} onChange={(event) => onChange({ ...normalizedField, value: event.target.value === "SI" })}>
              <option value="SI">Sì</option>
              <option value="NO">No</option>
            </VestaSelect>
          ) : options.length > 0 ? (
            <VestaSelect value={normalizedField.value || ""} options={options} onChange={(event) => onChange({ ...normalizedField, value: event.target.value })} placeholder="Seleziona default" />
          ) : (
            <input type={type === "number" ? "number" : "text"} min={type === "number" ? min : undefined} max={type === "number" ? max : undefined} step={type === "number" ? step : undefined} value={normalizedField.value ?? ""} onChange={(event) => onChange({ ...normalizedField, value: event.target.value })} placeholder="Valore default" />
          )}
        </div>
        {allowRequired ? <button type="button" className={`site_default_required${normalizedField.required === true ? " is_on" : ""}`} onClick={() => onChange({ ...normalizedField, required: normalizedField.required !== true })}>{normalizedField.required === true ? "Obbligatorio" : "Facoltativo"}</button> : null}
      </div>
    </div>
  );
}

function SiteDefaultSection({ title, children }) {
  return (
    <div className="site_default_group">
      <h4>{title}</h4>
      <div className="site_default_rows">{children}</div>
    </div>
  );
}

function DefaultServicesEditor({ serviceCatalog, value, onChange }) {
  const ids = Array.isArray(value) ? value.map((item) => String(item || "")) : [];
  const [selected, setSelected] = useState("");
  const activeCatalog = (Array.isArray(serviceCatalog) ? serviceCatalog : []).filter((item) => item?.active !== false);
  const available = activeCatalog.filter((item) => !ids.includes(String(item._id || "")));
  function addSelected() {
    if (!selected) return;
    onChange([...new Set([...ids, selected])]);
    setSelected("");
  }
  return (
    <div className="site_default_services_editor">
      <div className="site_default_copy">
        <strong>Servizi default In House</strong>
        <span>Servizi addebitati automaticamente quando una prenotazione entra in stato In House.</span>
      </div>
      <div className="site_default_services_controls">
        <div className="site_default_services_add_row">
          <VestaSelect value={selected} onChange={(event) => setSelected(event.target.value)}>
            <option value="">Seleziona servizio</option>
            {available.map((service) => <option key={service._id} value={service._id}>{service.description}</option>)}
          </VestaSelect>
          <button type="button" className="overlay_list_add_button" onClick={addSelected} disabled={!selected} aria-label="Aggiungi servizio default" title="Aggiungi servizio default"><svg className="overlay_action_icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v14M5 12h14" /></svg></button>
        </div>
        <div className="site_default_services_list">
          {ids.map((id) => {
            const service = activeCatalog.find((item) => String(item._id || "") === id);
            return (
              <div className="site_default_service_item" key={id}>
                <span>{service?.description || id}</span>
                <button type="button" className="overlay_contact_remove_button" onClick={() => onChange(ids.filter((valueId) => valueId !== id))} aria-label="Rimuovi servizio default" title="Rimuovi servizio default">×</button>
              </div>
            );
          })}
          {!ids.length && <div className="site_default_services_empty">Nessun servizio default configurato.</div>}
        </div>
      </div>
    </div>
  );
}

function SiteDataOverlay({ draft, error, validationErrors, customerTypeCatalog, paymentMethodCatalog, contactTypeCatalog, documentTypeCatalog, countryCatalog, languageCatalog, roomTypeCatalog, floorPreferenceCatalog, bedTypeCatalog, arrivalPreferenceCatalog, serviceCatalog, municipalities, postalCodes, onChange, onDefaultChange, onClose, onSave }) {
  const phoneContactTypeCatalog = (Array.isArray(contactTypeCatalog) ? contactTypeCatalog : []).filter((item) => ["telefono", "cellulare"].includes(String(item?.description || "").trim().toLocaleLowerCase("it")));
  const selectedMunicipality = findMunicipality(municipalities, draft.city, draft.province) || findMunicipality(municipalities, draft.city);
  const municipalityOptions = (Array.isArray(municipalities) ? municipalities : [])
    .filter((item) => item?.active !== false)
    .slice()
    .sort((a, b) => String(a.description || "").localeCompare(String(b.description || ""), "it") || String(a.provinceCode || "").localeCompare(String(b.provinceCode || ""), "it"))
    .map((item) => ({ value: String(item.code || `${item.description}|${item.provinceCode || ""}`), label: item.provinceCode ? `${item.description} (${item.provinceCode})` : item.description }));
  const municipalityValue = String(selectedMunicipality?.code || (draft.city ? `${draft.city}|${draft.province || ""}` : ""));
  if (draft.city && municipalityValue && !municipalityOptions.some((option) => option.value === municipalityValue)) municipalityOptions.push({ value: municipalityValue, label: draft.province ? `${draft.city} (${draft.province})` : draft.city });
  const postalOptions = postalCodeCatalogOptions(postalCodes, selectedMunicipality?.code || "", draft.cap);

  function selectMunicipality(event) {
    const selectedValue = String(event.target.value || "");
    const municipality = (Array.isArray(municipalities) ? municipalities : []).find((item) => String(item?.code || "") === selectedValue) || (() => {
      const [description, provinceCode] = selectedValue.split("|");
      return findMunicipality(municipalities, description, provinceCode);
    })();
    const firstPostalCode = postalCodeCatalogOptions(postalCodes, municipality?.code || "")[0]?.value || "";
    onChange("city", municipality?.description || "", { province: municipality?.provinceCode || "", cap: firstPostalCode });
  }

  return (
    <div className="overlay_backdrop">
      <div className="overlay_card booking_overlay_card site_data_overlay_card" role="dialog" aria-modal="true" aria-label="Sede" onFocusCapture={selectOverlayFieldValue}>
        <div className="overlay_header"><h2>{draft._id ? "MODIFICA SEDE" : "NUOVA SEDE"}</h2><button type="button" className="overlay_close_button" onClick={onClose} aria-label="Chiudi">×</button></div>
        <div className="overlay_body">
          <div className="overlay_section"><h3>Dati Principali</h3><div className="overlay_fields_grid">
            <label className="overlay_field"><OverlayFieldLabel required>Descrizione</OverlayFieldLabel><input value={draft.description || ""} onChange={(e) => onChange("description", e.target.value)} onBlur={() => onChange("description", String(draft.description || "").trim())} /></label>
            <label className={overlayFieldClass(validationErrors?.siteNature)}><OverlayFieldLabel required>Natura Sede</OverlayFieldLabel><VestaSelect value={draft.siteNature || ""} options={SITE_NATURE_OPTIONS} onChange={(event) => onChange("siteNature", event.target.value)} placeholder="Seleziona natura sede" /><OverlayFieldError message={validationErrors?.siteNature} /></label>
          </div></div>
          <div className="overlay_section"><h3>Ubicazione e Recapiti</h3><div className="overlay_fields_grid">
            <label className="overlay_field"><span>Indirizzo</span><input value={draft.address || ""} onChange={(e) => onChange("address", e.target.value)} onBlur={() => onChange("address", String(draft.address || "").trim())} /></label>
            <label className="overlay_field"><span>Civico</span><input value={draft.streetNumber || ""} onChange={(e) => onChange("streetNumber", e.target.value)} onBlur={() => onChange("streetNumber", String(draft.streetNumber || "").trim())} /></label>
            <label className="overlay_field"><span>Località</span><VestaSelect value={municipalityValue} options={municipalityOptions} onChange={selectMunicipality} placeholder="Seleziona località" /></label>
            <label className="overlay_field"><span>CAP</span><VestaSelect value={normalizePostalCodeValue(draft.cap)} options={postalOptions} onChange={(event) => onChange("cap", normalizePostalCodeValue(event.target.value))} placeholder="Seleziona CAP" /></label>
            <label className={overlayFieldClass(validationErrors?.phone)}><span>Telefono</span><input inputMode="numeric" value={draft.phone || ""} onChange={(e) => onChange("phone", e.target.value.replace(/[^0-9]/g, ""))} onBlur={() => onChange("phone", String(draft.phone || "").trim())} /><OverlayFieldError message={validationErrors?.phone} /></label>
            <label className={overlayFieldClass(validationErrors?.email)}><span>Mail</span><input type="email" value={draft.email || ""} onChange={(e) => onChange("email", e.target.value)} onBlur={() => onChange("email", String(draft.email || "").trim())} /><OverlayFieldError message={validationErrors?.email} /></label>
          </div></div>
          <div className="overlay_section site_defaults_section"><h3>Default Sede</h3><div className="site_defaults_compact">
            <SiteDefaultSection title="Cliente e Fatturazione">
              <DefaultRow label="Tipo anagrafica intestatario fattura" help="Tipo proposto quando crei un nuovo Intestatario Fattura." field={draft.defaults.customer.invoiceCustomerType} options={catalogOptions(customerTypeCatalog, draft.defaults.customer.invoiceCustomerType.value)} onChange={(value) => onDefaultChange("customer", "invoiceCustomerType", value)} />
              <DefaultRow label="Cliente e intestatario fattura uguali" help="Valore iniziale del flag di fatturazione nella prenotazione." type="checkbox" field={draft.defaults.customer.invoiceSameAsCustomer} onChange={(value) => onDefaultChange("customer", "invoiceSameAsCustomer", value)} />
            </SiteDefaultSection>
            <SiteDefaultSection title="Contatti e Documenti Cliente">
              <DefaultRow label="Tipo contatto cliente" help="Tipo della prima riga Telefono o Cellulare." field={draft.defaults.customer.contactType} options={catalogOptions(phoneContactTypeCatalog, draft.defaults.customer.contactType.value)} onChange={(value) => onDefaultChange("customer", "contactType", value)} />
              <DefaultRow label="Prefisso contatto cliente" help="Prefisso internazionale della prima riga contatto." field={draft.defaults.customer.countryPrefixId} options={countryPrefixOptions(countryCatalog, draft.defaults.customer.countryPrefixId.value)} onChange={(value) => onDefaultChange("customer", "countryPrefixId", value)} />
              <DefaultRow label="Tipo documento cliente" help="Tipo proposto nella prima riga documento della nuova anagrafica." field={draft.defaults.customer.documentType} options={catalogOptions(documentTypeCatalog, draft.defaults.customer.documentType.value)} onChange={(value) => onDefaultChange("customer", "documentType", value)} />
            </SiteDefaultSection>
            <SiteDefaultSection title="Preferenze Soggiorno">
              <DefaultRow label="Lingua preferita" help="Lingua proposta nelle preferenze soggiorno." field={draft.defaults.customer.preferredLanguage} options={catalogOptions(languageCatalog, draft.defaults.customer.preferredLanguage.value)} allowRequired onChange={(value) => onDefaultChange("customer", "preferredLanguage", value)} />
              <DefaultRow label="Tipo camera preferita" help="Tipologia camera proposta nelle preferenze soggiorno." field={draft.defaults.customer.preferredRoomTypeId} options={catalogIdOptions(roomTypeCatalog, draft.defaults.customer.preferredRoomTypeId.value)} allowRequired onChange={(value) => onDefaultChange("customer", "preferredRoomTypeId", value)} />
              <DefaultRow label="Piano preferito" help="Piano proposto nelle preferenze soggiorno." field={draft.defaults.customer.preferredFloor} options={catalogOptions(floorPreferenceCatalog, draft.defaults.customer.preferredFloor.value)} allowRequired onChange={(value) => onDefaultChange("customer", "preferredFloor", value)} />
              <DefaultRow label="Tipo letto preferito" help="Tipologia letto proposta nelle preferenze soggiorno." field={draft.defaults.customer.preferredBedTypeId} options={catalogIdOptions(bedTypeCatalog, draft.defaults.customer.preferredBedTypeId.value)} allowRequired onChange={(value) => onDefaultChange("customer", "preferredBedTypeId", value)} />
              <DefaultRow label="Preferenza arrivo" help="Preferenza arrivo proposta nella nuova anagrafica." field={draft.defaults.customer.arrivalPreference} options={catalogOptions(arrivalPreferenceCatalog, draft.defaults.customer.arrivalPreference.value)} allowRequired onChange={(value) => onDefaultChange("customer", "arrivalPreference", value)} />
            </SiteDefaultSection>
            <SiteDefaultSection title="Prenotazione e Documento Fiscale">
              <DefaultRow label="Metodo pagamento prenotazione" help="Metodo proposto sulle nuove prenotazioni della sede." field={draft.defaults.booking.paymentMethod} options={catalogOptions(paymentMethodCatalog, draft.defaults.booking.paymentMethod.value)} onChange={(value) => onDefaultChange("booking", "paymentMethod", value)} />
              <DefaultRow label="Stato nuova prenotazione" help="Stato iniziale proposto alla creazione della prenotazione." field={draft.defaults.booking.status} options={BOOKING_STATUS_OPTIONS} onChange={(value) => onDefaultChange("booking", "status", value)} />
              <DefaultRow label="Giorni soggiorno nuova prenotazione" help="Numero di giorni tra Check In e Check Out proposto all'apertura." type="number" field={draft.defaults.booking.stayDays} onChange={(value) => onDefaultChange("booking", "stayDays", value)} />
              <DefaultRow label="Documento fiscale default" help="Documento proposto nel Fast Check Out e nell'emissione fiscale." field={draft.defaults.booking.fiscalDocument} options={FISCAL_DOCUMENT_OPTIONS} onChange={(value) => onDefaultChange("booking", "fiscalDocument", value)} />
              <DefaultRow label="Aliquota IVA default" help="Aliquota IVA di fallback usata quando camera, servizio o riga documento non hanno un'aliquota valorizzata." type="number" min="0" max="100" step="0.01" field={draft.defaults.booking.defaultVatRate} onChange={(value) => onDefaultChange("booking", "defaultVatRate", { ...value, value: String(Math.min(100, Math.max(0, Number(value.value || 0)))) })} />
              <DefaultRow label="Documento storno default" help="Documento proposto per lo storno del soggiorno." field={draft.defaults.booking.reversalDocument} options={REVERSAL_DOCUMENT_OPTIONS} onChange={(value) => onDefaultChange("booking", "reversalDocument", value)} />
              <DefaultRow label="Pagamento A Saldo automatico Fast Check Out" help="Se attivo, il Fast Check Out inserisce automaticamente un pagamento A Saldo per la differenza ancora dovuta." type="checkbox" field={draft.defaults.booking.autoBalanceFastCheckout} onChange={(value) => onDefaultChange("booking", "autoBalanceFastCheckout", value)} />
              <DefaultServicesEditor serviceCatalog={serviceCatalog} value={draft.defaults.booking.defaultServiceIds} onChange={(value) => onDefaultChange("booking", "defaultServiceIds", value)} />
            </SiteDefaultSection>
          </div></div>
        </div>
        <div className="overlay_footer"><div className="overlay_error_message">{error}</div><button type="button" className="overlay_save_button overlay_save_close_button" onClick={onSave}>Salva e Chiudi</button></div>
      </div>
    </div>
  );
}


export default function Sites() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [overlayMode, setOverlayMode] = useState("");
  const [draft, setDraft] = useState(normalizeSiteDraft());
  const [overlayError, setOverlayError] = useState("");
  const [siteValidationErrors, setSiteValidationErrors] = useState({});
  const [confirmDeleteId, setConfirmDeleteId] = useState("");
  const [searchText, setSearchText] = useState("");
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);
  const [activeFilter, setActiveFilter] = useState("");
  const [columnFilters, setColumnFilters] = useState(EMPTY_FILTERS);
  const [sortState, setSortState] = useState({ key: "description", direction: "asc" });
  const [importExportMode, setImportExportMode] = useState("");
  const [customerTypeCatalog, setCustomerTypeCatalog] = useState([]);
  const [paymentMethodCatalog, setPaymentMethodCatalog] = useState([]);
  const [contactTypeCatalog, setContactTypeCatalog] = useState([]);
  const [documentTypeCatalog, setDocumentTypeCatalog] = useState([]);
  const [countryCatalog, setCountryCatalog] = useState([]);
  const [languageCatalog, setLanguageCatalog] = useState([]);
  const [roomTypeCatalog, setRoomTypeCatalog] = useState([]);
  const [floorPreferenceCatalog, setFloorPreferenceCatalog] = useState([]);
  const [bedTypeCatalog, setBedTypeCatalog] = useState([]);
  const [arrivalPreferenceCatalog, setArrivalPreferenceCatalog] = useState([]);
  const [serviceCatalog, setServiceCatalog] = useState([]);
  const [municipalities, setMunicipalities] = useState([]);
  const [postalCodes, setPostalCodes] = useState([]);
  const optionsMenuRef = useRef(null);
  const filterPanelRef = useRef(null);
  const pagePermissionLevel = getUserPageLevel(readStoredUser(), "site");
  const canEditPage = pagePermissionLevel >= 2;
  const canDeletePage = pagePermissionLevel >= 3;
  const blockedActionClass = (allowed) => allowed ? "" : " is_permission_blocked";
  const blockAction = () => {};

  async function loadSites() {
    setLoading(true); setError("");
    try {
      const [data, customerTypesData, paymentMethodsData, contactTypesData, documentTypesData, countriesData, languagesData, roomTypesData, floorPreferencesData, bedTypesData, arrivalPreferencesData, municipalitiesData, postalCodesData, serviceCatalogData] = await Promise.all([
        getSites({ includeInactive: true }),
        getCatalogItems("customer-types", { includeInactive: true }).catch(() => ({ items: [] })),
        getCatalogItems("payment-methods", { includeInactive: true }).catch(() => ({ items: [] })),
        getCatalogItems("contact-types", { includeInactive: true }).catch(() => ({ items: [] })),
        getCatalogItems("document-types", { includeInactive: true }).catch(() => ({ items: [] })),
        getCatalogItems("countries", { includeInactive: true }).catch(() => ({ items: [] })),
        getCatalogItems("languages", { includeInactive: true }).catch(() => ({ items: [] })),
        getCatalogItems("room-types", { includeInactive: true }).catch(() => ({ items: [] })),
        getCatalogItems("floor-preferences", { includeInactive: true }).catch(() => ({ items: [] })),
        getCatalogItems("bed-types", { includeInactive: true }).catch(() => ({ items: [] })),
        getCatalogItems("arrival-preferences", { includeInactive: true }).catch(() => ({ items: [] })),
        getCatalogItems("municipalities", { includeInactive: true }).catch(() => ({ items: [] })),
        getCatalogItems("postal-codes", { includeInactive: true }).catch(() => ({ items: [] })),
        getServiceCatalog({ includeInactive: true }).catch(() => ({ items: [] })),
      ]);
      setItems(Array.isArray(data?.items) ? data.items : []);
      setCustomerTypeCatalog(Array.isArray(customerTypesData?.items) ? customerTypesData.items : []);
      setPaymentMethodCatalog(Array.isArray(paymentMethodsData?.items) ? paymentMethodsData.items : []);
      setContactTypeCatalog(Array.isArray(contactTypesData?.items) ? contactTypesData.items : []);
      setDocumentTypeCatalog(Array.isArray(documentTypesData?.items) ? documentTypesData.items : []);
      setCountryCatalog(Array.isArray(countriesData?.items) ? countriesData.items : []);
      setLanguageCatalog(Array.isArray(languagesData?.items) ? languagesData.items : []);
      setRoomTypeCatalog(Array.isArray(roomTypesData?.items) ? roomTypesData.items : []);
      setFloorPreferenceCatalog(Array.isArray(floorPreferencesData?.items) ? floorPreferencesData.items : []);
      setBedTypeCatalog(Array.isArray(bedTypesData?.items) ? bedTypesData.items : []);
      setArrivalPreferenceCatalog(Array.isArray(arrivalPreferencesData?.items) ? arrivalPreferencesData.items : []);
      setMunicipalities(Array.isArray(municipalitiesData?.items) ? municipalitiesData.items : []);
      setPostalCodes(Array.isArray(postalCodesData?.items) ? postalCodesData.items : []);
      setServiceCatalog(Array.isArray(serviceCatalogData?.items) ? serviceCatalogData.items : []);
    } catch { setError("Impossibile caricare le sedi"); }
    finally { setLoading(false); }
  }

  useEffect(() => { loadSites(); }, []);

  useEffect(() => {
    if (!optionsOpen) return undefined;
    function closeOptions(event) {
      if (optionsMenuRef.current && !optionsMenuRef.current.contains(event.target)) {
        setOptionsOpen(false); setConfirmBulkDelete(false);
      }
    }
    document.addEventListener("pointerdown", closeOptions);
    return () => document.removeEventListener("pointerdown", closeOptions);
  }, [optionsOpen]);

  useEffect(() => {
    if (!activeFilter) return undefined;
    function closeFilters(event) {
      if (filterPanelRef.current && !filterPanelRef.current.contains(event.target)) setActiveFilter("");
    }
    document.addEventListener("pointerdown", closeFilters);
    return () => document.removeEventListener("pointerdown", closeFilters);
  }, [activeFilter]);

  useEffect(() => { if (selectedIds.length === 0) setConfirmBulkDelete(false); }, [selectedIds]);

  const filterOptions = useMemo(() => ({
    description: makeManagementFilterOptions(items, (item) => item.description),
    siteNature: makeManagementFilterOptions(items, (item) => siteNatureLabel(item.siteNature)),
    city: makeManagementFilterOptions(items, (item) => item.city),
    province: makeManagementFilterOptions(items, (item) => item.province),
    phone: makeManagementFilterOptions(items, (item) => item.phone),
    email: makeManagementFilterOptions(items, (item) => item.email),
  }), [items]);

  const filtered = useMemo(() => {
    return [...items].filter((item) => {
      const matchesSearch = matchesManagementSearch([item.description, siteNatureLabel(item.siteNature), item.city, item.province, item.address, item.streetNumber, item.cap, item.phone, item.email]
        , searchText);
      if (!matchesSearch) return false;
      return matchesManagementFilter(siteNatureLabel(item.siteNature), columnFilters.siteNature)
        && matchesManagementFilter(item.city, columnFilters.city)
        && matchesManagementFilter(item.province, columnFilters.province)
        && matchesManagementFilter(item.phone, columnFilters.phone, "text")
        && matchesManagementFilter(item.email, columnFilters.email, "text");
    }).sort((a, b) => String(a.description || "").localeCompare(String(b.description || ""), "it"));
  }, [items, searchText, columnFilters]);
  const siteSortGetters = useMemo(() => ({
    description: { getter: (item) => item.description, type: "text" },
    siteNature: { getter: (item) => siteNatureLabel(item.siteNature), type: "text" },
    city: { getter: (item) => item.city, type: "text" },
    province: { getter: (item) => item.province, type: "text" },
    phone: { getter: (item) => item.phone, type: "text" },
    email: { getter: (item) => item.email, type: "text" },
  }), []);
  const orderedFiltered = useMemo(() => sortManagementRows(filtered, sortState, siteSortGetters), [filtered, sortState, siteSortGetters]);

  function closeOverlay() { setOverlayMode(""); setOverlayError(""); setSiteValidationErrors({}); }
  function openSite(site = null) { if (!canEditPage) { blockAction(); return; } setOverlayError(""); setSiteValidationErrors({}); setConfirmDeleteId(""); setOptionsOpen(false); setConfirmBulkDelete(false); setDraft(normalizeSiteDraft(site || {})); setOverlayMode("site"); }
  function updateDraft(field, value, extraValues = {}) {
    setSiteValidationErrors((current) => current[field] ? { ...current, [field]: "" } : current);
    setDraft((current) => ({ ...current, [field]: value, ...extraValues }));
  }
  function updateDefault(section, field, value) {
    setDraft((current) => ({ ...current, defaults: { ...current.defaults, [section]: { ...current.defaults[section], [field]: value } } }));
  }
  function buildPayload(source) {
    const draft = normalizeSiteDraft(source);
    return {
      _id: draft._id,
      description: String(draft.description || "").trim(),
      siteNature: parseSiteNature(draft.siteNature),
      address: String(draft.address || "").trim(),
      streetNumber: String(draft.streetNumber || "").trim(),
      cap: String(draft.cap || "").trim(),
      city: String(draft.city || "").trim(),
      province: String(draft.province || "").trim().toUpperCase(),
      phone: String(draft.phone || "").trim(),
      email: String(draft.email || "").trim().toLowerCase(),
      defaults: cloneDefaults(draft.defaults),
      __v: draft.__v,
      active: draft.active !== false,
    };
  }
  async function persistSite(source, { close = true } = {}) {
    const payload = buildPayload(source);
    const validationErrors = {};
    if (!payload.description) validationErrors.description = "Descrizione obbligatoria";
    if (!payload.siteNature) validationErrors.siteNature = "Natura Sede obbligatoria";
    if (payload.phone && !/^\d{5,15}$/.test(payload.phone)) validationErrors.phone = "Il numero deve contenere da 5 a 15 cifre";
    if (payload.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(payload.email)) validationErrors.email = "Indirizzo email non valido";
    if (Object.keys(validationErrors).length) { setSiteValidationErrors(validationErrors); setOverlayError("Dati Errati e/o Incompleti"); return null; }
    try {
      const data = payload._id ? await updateSite(payload._id, payload) : await createSite(payload);
      const saved = data?.item;
      if (saved) setItems((current) => current.some((item) => String(item._id) === String(saved._id)) ? current.map((item) => String(item._id) === String(saved._id) ? saved : item) : [...current, saved]);
      if (close) closeOverlay();
      return saved;
    } catch (err) {
      const field = String(err?.response?.data?.field || "");
      const message = err?.response?.data?.error || "Dati Errati";
      if (err?.response?.status === 409 && err?.response?.data?.code === "VERSION_CONFLICT") await loadSites();
      if (field) setSiteValidationErrors((current) => ({ ...current, [field]: message }));
      setOverlayError(field ? "Dati Errati e/o Incompleti" : message);
      return null;
    }
  }
  function openImportExport(mode) { if (mode === "import" && !canEditPage) { blockAction(); return; } setImportExportMode(mode); setOptionsOpen(false); setConfirmBulkDelete(false); }
  async function importSites(records) {
    if (!canEditPage) throw new Error(UNAUTHORIZED_LABEL);
    let imported = 0;
    for (const record of records) {
      try {
        const data = await createSite(buildPayload({ ...EMPTY_SITE, ...record }));
        const saved = data?.item;
        if (saved) setItems((current) => current.some((item) => String(item._id) === String(saved._id)) ? current.map((item) => String(item._id) === String(saved._id) ? saved : item) : [...current, saved]);
        imported += 1;
      } catch (err) {
        throw new Error(`Riga ${imported + 2}: ${err?.response?.data?.error || "Dati Errati"}`);
      }
    }
    return { imported };
  }
  async function toggleActive(site) { if (!canEditPage) { blockAction(); return; } const next = normalizeSiteDraft(site); next.active = next.active === false; await persistSite(next, { close: false }); }
  async function removeSite(site) {
    setError("");
    try { await deleteSite(site._id); setItems((current) => current.filter((item) => String(item._id) !== String(site._id))); setSelectedIds((current) => current.filter((id) => String(id) !== String(site._id))); setConfirmDeleteId(""); }
    catch (err) { setError(err?.response?.data?.error || "Impossibile eliminare la sede"); }
  }
  async function removeSelected() {
    if (selectedIds.length === 0) return;
    setError("");
    try {
      await Promise.all(selectedIds.map((id) => deleteSite(id)));
      setItems((current) => current.filter((item) => !selectedIds.some((id) => String(id) === String(item._id))));
      setSelectedIds([]); setConfirmBulkDelete(false); setOptionsOpen(false);
    } catch (err) { setError(err?.response?.data?.error || "Impossibile eliminare le sedi selezionate"); }
  }
  function toggleFilter(columnKey) { setActiveFilter((current) => (current === columnKey ? "" : columnKey)); }
  function updateCheckFilter(columnKey, value, checked) {
    setColumnFilters((current) => {
      const values = Array.isArray(current[columnKey]) ? current[columnKey] : [];
      return { ...current, [columnKey]: checked ? [...values, value] : values.filter((item) => item !== value) };
    });
  }
  function clearColumnFilter(columnKey) { setColumnFilters((current) => ({ ...current, [columnKey]: [] })); }
  function toggleSort(columnKey) { setSortState((current) => ({ key: columnKey, direction: current.key === columnKey && current.direction === "asc" ? "desc" : "asc" })); }
  function resetSearchAndFilters() { setSearchText(""); setColumnFilters(EMPTY_FILTERS); setSortState({ key: "description", direction: "asc" }); setActiveFilter(""); setOptionsOpen(false); setConfirmBulkDelete(false); }
  function toggleSelected(id, checked) { setSelectedIds((current) => checked ? [...new Set([...current, id])] : current.filter((item) => String(item) !== String(id))); }
  function toggleAllFiltered(checked) {
    const filteredIds = orderedFiltered.map((item) => item._id);
    setSelectedIds((current) => checked ? [...new Set([...current, ...filteredIds])] : current.filter((id) => !filteredIds.some((itemId) => String(itemId) === String(id))));
  }

  const filteredIds = orderedFiltered.map((item) => item._id);
  const allFilteredSelected = filteredIds.length > 0 && filteredIds.every((id) => selectedIds.some((item) => String(item) === String(id)));
  const someFilteredSelected = filteredIds.some((id) => selectedIds.some((item) => String(item) === String(id)));
  const filterProps = { activeFilter, onToggle: toggleFilter, onCheckChange: updateCheckFilter, onClear: clearColumnFilter, onSort: toggleSort };
  const incrementalRows = useIncrementalRows(orderedFiltered);

  return (
    <section className="window_page bookings_page management_page management_shared_page sites_page">
      <div className="window_banner"><div className="window_banner_copy"><h1>SEDI</h1><p>Gestione sedi, stato operativo e default per anagrafiche clienti.</p></div></div>
      <div className="management_list_panel">
        <div className="booking_toolbar management_toolbar management_table_toolbar management_options_search_toolbar management_shared_toolbar">
          <OptionsMenu open={optionsOpen} menuRef={optionsMenuRef} disabled={selectedIds.length === 0} confirming={confirmBulkDelete} canEdit={canEditPage} canDelete={canDeletePage} blockedClass={blockedActionClass} onBlocked={blockAction} onToggle={() => setOptionsOpen((current) => !current)} onNew={() => openSite()} onReset={resetSearchAndFilters} onImport={() => openImportExport("import")} onExport={() => openImportExport("export")} onStartConfirm={() => setConfirmBulkDelete(true)} onCancelConfirm={() => setConfirmBulkDelete(false)} onConfirm={removeSelected} />
          <input className="management_search" value={searchText} onChange={(e) => setSearchText(formatManagementSearchInput(e.target.value))} placeholder="Cerca sedi (es. Padova, Roma)" />
          <div className="management_toolbar_results"><strong className="management_results_count">Risultati: {filtered.length}</strong></div>
        </div>
        <div className="management_board management_table_board">
          <div className="management_table_shell" role="table" aria-label="Elenco sedi" ref={incrementalRows.shellRef} onScroll={incrementalRows.handleScroll}>
            <div className="management_table_header management_shared_table_header management_sites_table_header" role="row" ref={filterPanelRef}>
              <div className="management_table_select_head" role="columnheader"><input type="checkbox" checked={allFilteredSelected} ref={(input) => { if (input) input.indeterminate = !allFilteredSelected && someFilteredSelected; }} onChange={() => toggleAllFiltered(!someFilteredSelected)} aria-label="Seleziona tutte le sedi filtrate" /></div>
              <ManagementFilterHeader columnKey="description" label="Descrizione" filterable={false} value={[]} sortDirection={sortState.key === "description" ? sortState.direction : ""} {...filterProps} />
              <ManagementFilterHeader columnKey="siteNature" label="Natura Sede" options={filterOptions.siteNature} value={columnFilters.siteNature} sortDirection={sortState.key === "siteNature" ? sortState.direction : ""} {...filterProps} />
              <ManagementFilterHeader columnKey="city" label="Località" options={filterOptions.city} value={columnFilters.city} sortDirection={sortState.key === "city" ? sortState.direction : ""} {...filterProps} />
              <ManagementFilterHeader columnKey="province" label="Provincia" options={filterOptions.province} value={columnFilters.province} sortDirection={sortState.key === "province" ? sortState.direction : ""} {...filterProps} />
              <ManagementFilterHeader columnKey="phone" label="Telefono" value={columnFilters.phone} filterType="text" sortDirection={sortState.key === "phone" ? sortState.direction : ""} {...filterProps} />
              <ManagementFilterHeader columnKey="email" label="Mail" value={columnFilters.email} filterType="text" sortDirection={sortState.key === "email" ? sortState.direction : ""} {...filterProps} />
              <div className="management_table_actions_head" role="columnheader">Azioni</div>
            </div>
            {loading && <ManagementListLoading label="Caricamento Sedi" />}{!loading && error && <div className="management_table_visible_status is_error">{error}</div>}{!loading && !error && filtered.length === 0 && <div className="management_table_visible_status">Nessun Risultato Trovato</div>}<div className="management_table_body" role="rowgroup">
              
              
              {incrementalRows.rows.map((item) => {
                const isConfirming = String(confirmDeleteId) === String(item._id);
                return (
                  <article key={item._id} className={`management_table_row management_sites_table_row${item.active === false ? " is_inactive" : ""}${selectedIds.some((id) => String(id) === String(item._id)) ? " is_selected" : ""}`} data-vesta-focus-id={item._id} role="row">
                    <div className="management_table_cell management_table_select_cell" role="cell"><input type="checkbox" checked={selectedIds.some((id) => String(id) === String(item._id))} onChange={(event) => toggleSelected(item._id, event.target.checked)} aria-label={`Seleziona sede ${item.description}`} /></div>
                    <div className="management_table_cell management_table_title_cell" role="cell"><strong>{displayValue(item.description)}</strong></div>
                    <div className="management_table_cell" role="cell">{siteNatureLabel(item.siteNature)}</div>
                    <div className="management_table_cell" role="cell">{displayValue(item.city)}</div>
                    <div className="management_table_cell" role="cell">{displayValue(item.province)}</div>
                    <div className="management_table_cell" role="cell">{displayValue(item.phone)}</div>
                    <div className="management_table_cell" role="cell">{displayValue(item.email)}</div>
                    <div className="management_table_actions management_sites_actions" role="cell">
                      <button type="button" className={`management_action_button ${item.active === false ? "management_action_success" : "management_action_danger"}${blockedActionClass(canEditPage)}`} data-disabled-tooltip={UNAUTHORIZED_LABEL} onClick={() => toggleActive(item)}>{item.active === false ? "Attiva" : "Disattiva"}</button>{isConfirming ? <><button type="button" className="management_action_button management_action_cancel" onClick={() => setConfirmDeleteId("")}>Annulla</button><button type="button" className={`management_action_button management_action_confirm${blockedActionClass(canDeletePage)}`} data-disabled-tooltip={UNAUTHORIZED_LABEL} onClick={() => removeSite(item)}>Conferma</button></> : <><button type="button" className={`management_action_button management_action_primary${blockedActionClass(canEditPage)}`} data-disabled-tooltip={UNAUTHORIZED_LABEL} onClick={() => openSite(item)}>Modifica</button><button type="button" className={`management_action_button management_action_danger${blockedActionClass(canDeletePage)}`} data-disabled-tooltip={UNAUTHORIZED_LABEL} onClick={() => canDeletePage ? setConfirmDeleteId(item._id) : blockAction()}>Elimina</button></>}
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        </div>
      </div>
      {overlayMode === "site" && <SiteDataOverlay draft={draft} error={overlayError} validationErrors={siteValidationErrors} customerTypeCatalog={customerTypeCatalog} paymentMethodCatalog={paymentMethodCatalog} contactTypeCatalog={contactTypeCatalog} documentTypeCatalog={documentTypeCatalog} countryCatalog={countryCatalog} languageCatalog={languageCatalog} roomTypeCatalog={roomTypeCatalog} floorPreferenceCatalog={floorPreferenceCatalog} bedTypeCatalog={bedTypeCatalog} arrivalPreferenceCatalog={arrivalPreferenceCatalog} serviceCatalog={serviceCatalog} municipalities={municipalities} postalCodes={postalCodes} onChange={updateDraft} onDefaultChange={updateDefault} onClose={closeOverlay} onSave={() => persistSite(draft)} />}
      {importExportMode && <ImportExportOverlay mode={importExportMode} title="Sedi" entityLabel="Sedi" fileBaseName="vesta_sedi" fields={SITE_IMPORT_EXPORT_FIELDS} rows={orderedFiltered} onClose={() => setImportExportMode("")} onImport={importSites} />}
    </section>
  );
}
